import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth'
import { analytics } from '../../config/firebase'
import { auth } from '../../config/firebase'
import { logEvent } from 'firebase/analytics'
import type { AuthContextType, User } from './types'
import { mapFirebaseUserToUser } from './types'
import { api } from '../../services/api'

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  login: async () => {},
  logout: async () => {},
  isAuthenticated: false,
  checkSubscription: async () => {},
})

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext)

// Function to check if extension is available
const checkExtensionAvailability = (extensionId: string) => {
  if (typeof chrome !== 'undefined' && chrome.runtime) {
    try {
      console.log('Checking extension availability:', extensionId)
      chrome.runtime.sendMessage(
        extensionId,
        { action: 'ping' },
        (response) => {
          if (chrome.runtime.lastError) {
            console.log(
              'Extension check failed:',
              chrome.runtime.lastError.message,
            )
          } else if (response && response.pong) {
            console.log('Extension found and responding!')
          } else {
            console.log('Extension found but not responding correctly')
          }
        },
      )
    } catch (error) {
      console.log('Exception checking extension:', error)
    }
  } else {
    console.log('Chrome runtime not available')
  }
}

// Function to send user ID to Chrome extension if installed
const sendUserIdToExtension = (userId: string | null) => {
  // Check if Chrome extension API is available
  if (typeof chrome !== 'undefined' && chrome.runtime) {
    try {
      // Use environment variable or hardcode your extension ID for testing
      // Make sure this matches the variable name in your .env file
      const extensionId = process.env.CHROME_EXTENSION_ID

      chrome.runtime.sendMessage(
        extensionId,
        { action: 'setUserId', userId },
        (response) => {
          if (chrome.runtime.lastError) {
            // Extension might not be installed - this is normal
            console.log(
              'Extension communication:',
              chrome.runtime.lastError.message,
            )
          } else if (response) {
            console.log('Extension response:', response)
          }
        },
      )
    } catch (error) {
      // Log details about the error
      console.log('Error communicating with extension:', error)
    }
  } else {
    console.log('Chrome runtime API not available')
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const checkSubscription = async () => {
    if (user?.uid) {
      try {
        console.log(`Checking subscription status for user ${user.uid}...`)
        const subscriptionStatus = await api.getSubscriptionStatus(user.uid)
        console.log('Subscription status received:', subscriptionStatus)

        setUser((prev) =>
          prev ? { ...prev, subscription: subscriptionStatus } : null,
        )

        return subscriptionStatus
      } catch (error) {
        console.error('Error checking subscription:', error)
        return null
      }
    }
    return null
  }

  // Check extension availability when component mounts
  useEffect(() => {
    // Use environment variable or hardcode your extension ID for testing
    const extensionId = process.env.CHROME_EXTENSION_ID
    if (extensionId) {
      checkExtensionAvailability(extensionId)
    } else {
      console.log('No extension ID found in environment variables')
    }
  }, [])

  // Listen for auth state changes when the component mounts
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        setLoading(true)

        if (firebaseUser) {
          // User is signed in
          const mappedUser = mapFirebaseUserToUser(firebaseUser)

          // Get subscription status
          try {
            const subscriptionStatus = await api.getSubscriptionStatus(
              mappedUser.uid,
            )
            mappedUser.subscription = subscriptionStatus
          } catch (error) {
            console.error('Error getting subscription status:', error)
          }

          setUser(mappedUser)

          // Store user ID in localStorage for extension access
          localStorage.setItem('makeMyResumeUserId', mappedUser.uid)

          // Attempt to send user ID to Chrome extension if installed
          sendUserIdToExtension(mappedUser.uid)

          // Log sign-in event in analytics
          logEvent(analytics, 'login', {
            method: 'Google',
          })
        } else {
          // User is signed out
          setUser(null)

          // Remove user ID from localStorage
          localStorage.removeItem('makeMyResumeUserId')

          // Notify extension that user has logged out
          sendUserIdToExtension(null)
        }

        setLoading(false)
        setError(null)
      },
      (error) => {
        console.error('Auth state change error:', error)
        setError(error.message)
        setLoading(false)
      },
    )

    // Cleanup subscription on unmount
    return () => unsubscribe()
  }, [])

  // Sign in with Google
  const login = async (): Promise<void> => {
    try {
      setLoading(true)
      const provider = new GoogleAuthProvider()
      await signInWithPopup(auth, provider)
      // Auth state change listener will handle setting the user
    } catch (error: any) {
      console.error('Login error:', error)
      setError(error.message)
      setLoading(false)
      throw error
    }
  }

  // Sign out
  const logout = async (): Promise<void> => {
    try {
      setLoading(true)
      await signOut(auth)
      // Auth state change listener will handle setting the user to null
      // Log sign-out event in analytics
      logEvent(analytics, 'logout')
    } catch (error: any) {
      console.error('Logout error:', error)
      setError(error.message)
      setLoading(false)
      throw error
    }
  }

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
    checkSubscription,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
